import axios from 'axios'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, skyProductURL, skyQueryKey } from './skyEndpoints'
import type { MultiPipelineRecordsResponse } from '../../types/gotypes'
import type { LabelQuery } from '../../components/social-media/types'

/**
 * How many coded comments the current scope holds — the headline number, not
 * the table's.
 *
 * 🔴 WHY IT IS NOT `useSkyComments().totalCount`. That hook is the comment
 * STREAM, and it is deliberately deferred: the stream sits behind a collapsed
 * section and there is no reason to pull a page of records nobody asked for.
 * Its `enabled` flag is the section's expanded state — so with the section shut
 * it never fetches, `query.data` is undefined, and `totalCount` falls back to
 * its `?? 0`.
 *
 * The page then rendered "0 comments" beside "1,180 videos" on a job holding
 * 41,307 of them. Nothing failed: a not-loaded count and a genuinely-zero count
 * were rendered identically, which is the whole bug. A deferred fetch is a fine
 * thing to have; letting its absence read as a real answer is not.
 *
 * So the count is its own request. It asks for ONE record and reads only
 * `total_count` — the server computes the same total either way, and the wire
 * cost of the page of rows is what the deferral was actually protecting.
 *
 * It deliberately carries NO search text. This number describes the SCOPE (this
 * department, these filters), which is what the header and the section label
 * are naming; the table shows its own "1–25 of N" for a search within it.
 */
export function useSkyCommentCount(
  product: string,
  labelQuery?: LabelQuery,
  enabled = true,
): { count: number; isLoading: boolean; isUpdating: boolean } {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  const queryKey = skyQueryKey(product, 'comment-count', { labelsKey })
  const query = useQuery({
    queryKey,
    enabled: Boolean(product && account.user && enabled),
    retry: 1,
    retryDelay: 1000,
    placeholderData: keepWhileOnlyFilterChanges<MultiPipelineRecordsResponse>(queryKey),
    queryFn: async ({ signal }): Promise<MultiPipelineRecordsResponse> => {
      const res = await axios.post<MultiPipelineRecordsResponse>(
        skyProductURL(product, '/pipelines/records'),
        { labelQuery, state: 'completed', limit: 1, offset: 0 },
        { signal },
      )
      return res.data
    },
  })

  return {
    count: query.data?.total_count ?? 0,
    isLoading: query.isLoading,
    isUpdating: query.isPlaceholderData,
  }
}

export default useSkyCommentCount
